import React from 'react';
import { Ability } from '../../types';
import { DamageTypeBadge } from '../ui/DamageTypeBadge';
import { KeywordBadge } from '../ui/KeywordBadge';

export interface AbilityCardProps {
  /** The ability to display */
  ability: Ability;
  /** Remaining uses for this ability (from abilityUsages tracking) */
  remainingUses?: number;
  /** Whether the ability can be used (own character) */
  isEditable: boolean;
  /** Callback when the ability is used */
  onUseAbility?: () => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * AbilityCard Component
 * 
 * Displays ability information with usage tracking:
 * - Ability name, damage, range, description
 * - Remaining uses for limited abilities
 * - Use ability button for own character
 * - Visual indication for exhausted abilities
 * - Sub-abilities display
 * 
 * Requirements: 3.8, 9.1, 9.2, 9.4
 */
export const AbilityCard: React.FC<AbilityCardProps> = ({
  ability,
  remainingUses,
  isEditable,
  onUseAbility,
  className = '',
}) => {
  // Determine if ability has limited uses
  const hasLimitedUses = ability.maxUses !== undefined && ability.maxUses !== null;
  
  // Calculate actual remaining uses
  const actualRemainingUses = hasLimitedUses
    ? (remainingUses !== undefined ? remainingUses : ability.usesRemaining ?? ability.maxUses ?? 0)
    : null;
  
  // Check if ability is exhausted
  const isExhausted = hasLimitedUses && actualRemainingUses !== null && actualRemainingUses <= 0;

  // Format range display
  const formatRange = (range: { min: number; max: number; text?: string }) => {
    if (range.text) {
      return range.text;
    }
    if (range.min === range.max) {
      return `${range.min} pace${range.min !== 1 ? 's' : ''}`;
    }
    return `${range.min}-${range.max} paces`;
  };

  const handleUseAbility = () => {
    if (!isExhausted && isEditable && onUseAbility) {
      onUseAbility();
    }
  };

  return (
    <div
      className={`
        ability-card
        rounded-xl border
        transition-all duration-300
        ${isExhausted
          ? 'bg-gray-800/20 border-gray-700/30 opacity-50'
          : 'bg-gray-800/40 border-gray-700/60 hover:border-primary-500/30 hover:bg-gray-800/60 hover:shadow-lg hover:shadow-primary-500/5'
        }
        ${className}
      `}
      data-testid="ability-card"
      data-ability-id={ability.abilityId}
      data-exhausted={isExhausted}
    >
      {/* Main Ability Content */}
      <div className="p-5">
        {/* Header: Name and Stats */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {ability.requiresAttackRoll && <span className="text-lg">🎲</span>}
              <h4 
                className={`text-lg font-semibold ${isExhausted ? 'text-gray-500' : 'text-white'}`}
                data-testid="ability-name"
              >
                {ability.name}
              </h4>
            </div>
            
            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* Damage */}
              {ability.damage > 0 && (
                <span className="ability-stat damage" data-testid="ability-damage">
                  ⚔️ {ability.damage} dmg
                </span>
              )}
              
              {/* Damage Type */}
              {ability.damageType && (
                <DamageTypeBadge 
                  damageType={ability.damageType} 
                  showTooltip={true}
                  className="ability-stat"
                />
              )}
              
              {/* Range */}
              <span className="ability-stat range" data-testid="ability-range">
                📏 {formatRange(ability.range)}
              </span>
              
              {/* Phases */}
              <span className={`ability-stat ${isExhausted ? 'opacity-50' : ''}`}>
                ⏱️ {ability.requiresPhases} phase{ability.requiresPhases !== 1 ? 's' : ''}
              </span>
              
              {/* Max Uses */}
              {hasLimitedUses && (
                <span className="ability-stat max-uses">
                  🔄 {ability.maxUses} uses
                </span>
              )}
              
              {/* Will Attack */}
              {ability.isWillAttack && (
                <span className="ability-stat will-attack">
                  ✨ Will Attack
                </span>
              )}
            </div>
          </div>

          {/* Usage Counter */}
          {hasLimitedUses && (
            <div 
              className={`
                flex flex-col items-center px-4 py-2 rounded-xl
                ${isExhausted 
                  ? 'bg-red-900/20 border border-red-500/30' 
                  : 'bg-primary-900/20 border border-primary-500/30'
                }
              `}
              data-testid="usage-counter"
            >
              <span 
                className={`text-2xl font-bold ${isExhausted ? 'text-red-400' : 'text-primary-400'}`}
                data-testid="remaining-uses"
              >
                {actualRemainingUses}
              </span>
              <span className={`text-xs font-medium ${isExhausted ? 'text-red-300' : 'text-primary-300'}`}>
                / {ability.maxUses}
              </span>
            </div>
          )}
        </div>

        {/* Keywords */}
        {ability.keywords && ability.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ability-keywords">
            {ability.keywords.map((keyword, index) => (
              <span
                key={`${keyword}-${index}`}
                className="keyword-badge ability-keyword-badge"
              >
                <KeywordBadge keyword={keyword} showTooltip={true} />
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        <p 
          className={`text-sm leading-relaxed ${isExhausted ? 'text-gray-600' : 'text-gray-400'}`}
          data-testid="ability-description"
        >
          {ability.description}
        </p>

        {/* Exhausted Indicator */}
        {isExhausted && (
          <div 
            className="flex items-center justify-center py-3 px-4 mt-4 bg-red-900/20 border border-red-500/30 rounded-xl text-red-400 text-sm font-semibold"
            data-testid="exhausted-indicator"
          >
            <span className="mr-2">⛔</span>
            Exhausted - No uses remaining
          </div>
        )}

        {/* Use Ability Button */}
        {isEditable && !isExhausted && onUseAbility && (
          <button
            type="button"
            onClick={handleUseAbility}
            className="
              w-full mt-4 px-4 py-3 rounded-xl
              bg-gradient-to-b from-primary-500/20 to-primary-600/20
              hover:from-primary-500/30 hover:to-primary-600/30
              border border-primary-500/40 hover:border-primary-500/60
              text-primary-400 font-semibold text-sm
              transition-all duration-200
              shadow-sm hover:shadow-md active:scale-[0.98]
            "
            data-testid="use-ability-button"
          >
            Use Ability
          </button>
        )}
      </div>

      {/* Sub-abilities Section */}
      {ability.subAbilities && ability.subAbilities.length > 0 && (
        <div className="sub-abilities-section border-t-2 border-gray-700/50 bg-gray-900/30 rounded-b-xl">
          <div className="px-5 py-4">
            <h5 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <span className="text-primary-400">↳</span>
              Sub-Abilities ({ability.subAbilities.length})
            </h5>
            <div className="space-y-3">
              {ability.subAbilities.map((subAbility, index) => (
                <SubAbilityCard 
                  key={subAbility.abilityId || index} 
                  subAbility={subAbility}
                  formatRange={formatRange}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-ability card component
interface SubAbilityCardProps {
  subAbility: Ability;
  formatRange: (range: { min: number; max: number; text?: string }) => string;
}

const SubAbilityCard: React.FC<SubAbilityCardProps> = ({ subAbility, formatRange }) => {
  return (
    <div className="sub-ability-detail-card bg-gray-800/40 border border-gray-700/50 rounded-lg p-4 hover:border-primary-500/20 transition-all duration-200">
      {/* Sub-ability Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <strong className="text-white text-sm font-semibold">
          {subAbility.requiresAttackRoll && <span className="mr-1">🎲</span>}
          {subAbility.name}
        </strong>
        <div className="flex flex-wrap gap-1.5">
          {subAbility.damage > 0 && (
            <span className="sub-ability-stat damage">⚔️ {subAbility.damage}</span>
          )}
          {subAbility.damageType && (
            <DamageTypeBadge
              damageType={subAbility.damageType}
              showTooltip={true}
              className="sub-ability-stat"
            />
          )}
          <span className="sub-ability-stat range">📏 {formatRange(subAbility.range)}</span>
          {subAbility.maxUses !== undefined && subAbility.maxUses !== null && (
            <span className="sub-ability-max-uses">🔄 {subAbility.maxUses}</span>
          )}
          {subAbility.isWillAttack && (
            <span className="will-badge">✨ Will</span>
          )}
        </div>
      </div>
      
      {/* Sub-ability Keywords */}
      {subAbility.keywords && subAbility.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {subAbility.keywords.map((keyword, keywordIndex) => (
            <span key={keywordIndex} className="keyword-badge ability-keyword-badge text-xs">
              <KeywordBadge keyword={keyword} showTooltip={true} />
            </span>
          ))}
        </div>
      )}
      
      {/* Sub-ability Description */}
      <p className="text-gray-400 text-xs leading-relaxed">{subAbility.description}</p>
    </div>
  );
};
